import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import ResellerShell from './ResellerShell'
import ManagedInviteModal from '../../components/ManagedInviteModal'
import Toggle from '../../components/Toggle'
import { RESELLER_MENU_ITEMS } from '../../config/resellerMenu'
import { useLanguage } from '../../context/LanguageContext'
import { tournamentApi } from '../../services/api'
import { setActingAccountId, getActingAccountId } from '../../services/actingAccount'

const PENDING_STATUSES = new Set(['sent', 'opened'])

function filterLabel(t, s) {
  switch (s) {
    case 'pending': return t('sp_status_pending', 'Pending')
    case 'accepted': return t('sp_managed_accepted', 'Accepted')
    case 'reclaimed': return t('sp_managed_reclaimed', 'Reclaimed')
    case 'expired': return t('sp_invite_expired', 'Expired')
    case 'all': return t('filter_all', 'All')
    default: return s[0].toUpperCase() + s.slice(1)
  }
}

// An accepted invite whose child account is still operating (active) vs one
// that has been reclaimed/disabled (closed/suspended, or the account is gone).
const isLiveChild = (i) => i.status === 'accepted' && i.childStatus === 'active'
const isReclaimedChild = (i) => i.status === 'accepted' && i.childStatus !== 'active'

/**
 * Parent-managed child accounts hub (EPIC #92, P3).
 * Reseller-flavor invites + pending invites table. Course invites also
 * appear here; course entry point lives on each grouping tile too.
 */
export default function ManagedAccounts() {
  const { t } = useLanguage()
  const [account, setAccount] = useState(null)
  const [groupings, setGroupings] = useState([])
  const [invites, setInvites] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('pending')
  const [showResellerModal, setShowResellerModal] = useState(false)
  const [recentLink, setRecentLink] = useState(null)
  const [notice, setNotice] = useState(null)

  const canInvite = account?.CanInvite !== 0 && account?.CanInvite !== false

  function reload() {
    setLoading(true)
    setError(null)
    Promise.all([
      tournamentApi.get('/portal/reseller/me'),
      tournamentApi.get('/portal/reseller/groupings'),
      tournamentApi.get('/portal/reseller/invites'),
    ])
      .then(([me, gr, inv]) => {
        setAccount(me.data?.account || null)
        setGroupings(gr.data?.groupings || [])
        setInvites(inv.data?.invites || [])
      })
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }

  useEffect(reload, [])

  const filtered = invites.filter(i => {
    if (filter === 'pending') return PENDING_STATUSES.has(i.status)
    if (filter === 'accepted') return isLiveChild(i)
    if (filter === 'reclaimed') return isReclaimedChild(i)
    if (filter === 'expired') return i.status === 'expired'
    return true
  })

  async function handleResend(id) {
    try {
      const { data } = await tournamentApi.post(`/portal/reseller/invites/${id}/resend`, {})
      setRecentLink({ url: data.acceptUrl, kind: 'resent', emailSent: data.emailSent })
      reload()
    } catch (err) {
      alert(err?.response?.data?.message || err.message)
    }
  }

  async function handleRevoke(id) {
    if (!confirm(t('sp_managed_revoke_confirm', 'Revoke this invitation? The link will stop working.'))) return
    try {
      await tournamentApi.delete(`/portal/reseller/invites/${id}`)
      reload()
    } catch (err) {
      alert(err?.response?.data?.message || err.message)
    }
  }

  // EPIC #92 P5 — "act-as": scope the portal to this managed child and reload.
  // The server re-verifies the parent relationship on every request.
  function manageAccount(childId) {
    setActingAccountId(childId)
    window.location.assign('/portal/reseller')
  }

  // EPIC #92 P5 — reclaim: remove the child reseller and pull its groupings +
  // packages back into this (parent) account.
  async function handleReclaim(i) {
    const who = i.name || i.email
    if (!confirm(t('sp_managed_reclaim_confirm', 'Remove {name} and move their groupings + packages back to your account? This closes their account.', { name: who }))) return
    try {
      const { data } = await tournamentApi.post(`/portal/reseller/managed-children/${i.acceptedResellerId}/reclaim`, {})
      // If we happened to be acting-as the reclaimed child, drop that scope.
      if (getActingAccountId() === i.acceptedResellerId) setActingAccountId(null)
      setNotice(
        data.mode === 'copied'
          ? t('sp_reclaim_copied_notice', '{name} had {bookings} booking(s), so its {packages} package(s) were copied back to your account and the child was disabled (its bookings stay intact).', { name: data.childName || who, bookings: data.bookings, packages: data.clonedPackages })
          : t('sp_reclaim_moved_notice', '{name} was removed and its {packages} package(s) moved back to your account.', { name: data.childName || who, packages: data.packagesMoved })
      )
      reload()
    } catch (err) {
      alert(err?.response?.data?.message || err.message)
    }
  }

  function onInviteCreated(data) {
    setRecentLink({
      url: data.acceptUrl,
      kind: 'created',
      emailSent: data.emailSent,
      emailError: data.emailError,
    })
    reload()
  }

  if (!loading && account && !canInvite) {
    return (
      <ResellerShell title={t('sp_managed_title', 'Managed accounts')}>
        <div style={infoBox}>
          {t('sp_managed_cannot_invite', 'This account was set up by a parent reseller and cannot send invitations. Use the portal to manage your assigned groupings and bookings.')}
        </div>
      </ResellerShell>
    )
  }

  return (
    <ResellerShell title={t('sp_managed_title', 'Managed accounts')}>
      <p style={{ color: '#64748b', maxWidth: 720, marginTop: 0, fontSize: 14, lineHeight: 1.6 }}>
        {t('sp_managed_intro', 'Invite a course to run a single grouping you built, or invite another reseller to operate a full storefront across multiple groupings. You keep co-management access and set the economics (markup lands in a later phase).')}
      </p>

      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setShowResellerModal(true)}
          disabled={!canInvite || groupings.length === 0}
          style={{ ...primaryBtn, opacity: (!canInvite || groupings.length === 0) ? 0.5 : 1 }}
        >
          {t('sp_managed_invite_reseller_btn', '+ Invite reseller')}
        </button>
        <Link to="/portal/reseller/groupings" style={secondaryLink}>
          {t('sp_managed_invite_course_link', 'Invite a course from a grouping tile')}
        </Link>
      </div>

      {groupings.length === 0 && (
        <div style={{ ...infoBox, marginBottom: 20 }}>
          {t('sp_managed_need_grouping', 'Create at least one grouping before sending invitations.')}{' '}
          <Link to="/portal/reseller/groupings">{t('sp_managed_go_to_groupings', 'Go to groupings')}</Link>
        </div>
      )}

      {recentLink && (
        <div style={successBox}>
          <div style={{ fontWeight: 600, color: '#065f46', marginBottom: 6 }}>
            {recentLink.kind === 'resent' ? t('sp_managed_invitation_resent', 'Invitation resent') : t('sp_managed_invitation_created', 'Invitation created')}
            {recentLink.emailSent === false && ' ' + t('sp_managed_email_not_sent', '(email not sent — copy the link below)')}
          </div>
          {recentLink.emailError && (
            <div style={{ fontSize: 12, color: '#b45309', marginBottom: 8 }}>{recentLink.emailError}</div>
          )}
          <code style={linkCode}>{recentLink.url}</code>
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => navigator.clipboard.writeText(recentLink.url)} style={secondaryBtn}>
              {t('sp_managed_copy_link', 'Copy link')}
            </button>
            <button type="button" onClick={() => setRecentLink(null)} style={secondaryBtn}>{t('sp_managed_dismiss', 'Dismiss')}</button>
          </div>
        </div>
      )}

      {notice && (
        <div style={successBox}>
          <div style={{ fontSize: 13, color: '#065f46', lineHeight: 1.5 }}>{notice}</div>
          <div style={{ marginTop: 10 }}>
            <button type="button" onClick={() => setNotice(null)} style={secondaryBtn}>{t('sp_managed_dismiss', 'Dismiss')}</button>
          </div>
        </div>
      )}

      {error && <div style={errorBox}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>{t('sp_managed_invitations_heading', 'Invitations')}</h2>
        <div style={tabBar}>
          {['pending', 'accepted', 'reclaimed', 'expired', 'all'].map(s => (
            <button key={s} type="button" onClick={() => setFilter(s)} style={tabBtn(filter === s)}>
              {filterLabel(t, s)}
            </button>
          ))}
        </div>
      </div>

      {loading
        ? <div style={empty}>{t('sp_loading_short', 'Loading…')}</div>
        : filtered.length === 0
          ? <div style={empty}>{filter === 'all' ? t('sp_managed_no_invitations_all', 'No invitations.') : t('sp_managed_no_invitations_filtered', 'No {filter} invitations.', { filter: filterLabel(t, filter).toLowerCase() })}</div>
          : (
            <div style={tableWrap}>
              <table style={tbl}>
                <thead>
                  <tr>
                    <th style={th}>{t('sp_email', 'Email')}</th>
                    <th style={th}>{t('type', 'Type')}</th>
                    <th style={th}>{t('groupings', 'Groupings')}</th>
                    <th style={th}>{t('status', 'Status')}</th>
                    <th style={th}>{t('sp_markup_col', 'Markup')}</th>
                    <th style={th}>{t('sp_menu_col', 'Menu access')}</th>
                    <th style={th}>{t('sp_invite_sent', 'Sent')}</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(i => (
                    <tr key={i.id} style={tr}>
                      <td style={td}>
                        {i.email}
                        {i.name && <div style={{ fontSize: 12, color: '#64748b' }}>{i.name}</div>}
                      </td>
                      <td style={td}>
                        <TypeBadge type={i.inviteType} />
                      </td>
                      <td style={td}>
                        {(i.groupings || []).map(g => g.name).join(', ') || '—'}
                      </td>
                      <td style={td}><StatusBadge status={i.status} /></td>
                      <td style={td}>
                        <MarkupCell
                          invite={i}
                          editable={PENDING_STATUSES.has(i.status) || (i.status === 'accepted' && i.childStatus === 'active')}
                          onSaved={reload}
                        />
                      </td>
                      <td style={td}>
                        <MenuAccessCell
                          invite={i}
                          editable={PENDING_STATUSES.has(i.status) || (i.status === 'accepted' && i.childStatus === 'active')}
                          onSaved={reload}
                        />
                      </td>
                      <td style={td}>{i.sentAt ? new Date(i.sentAt).toLocaleString() : '—'}</td>
                      <td style={td}>
                        {PENDING_STATUSES.has(i.status) && (
                          <PendingInviteActions
                            invite={i}
                            onResend={() => handleResend(i.id)}
                            onRevoke={() => handleRevoke(i.id)}
                          />
                        )}
                        {i.status === 'accepted' && i.acceptedResellerId && i.childStatus === 'active' && (
                          <>
                            <button type="button" onClick={() => manageAccount(i.acceptedResellerId)} style={manageBtn}>
                              {t('sp_managed_manage_this_account', 'Manage this account →')}
                            </button>
                            <button type="button" onClick={() => handleReclaim(i)} style={{ ...secondaryBtn, marginLeft: 6, color: '#dc2626', borderColor: '#fecaca' }}>
                              {t('sp_managed_reclaim', 'Remove & reclaim')}
                            </button>
                          </>
                        )}
                        {i.status === 'accepted' && (i.childStatus === 'closed' || i.childStatus === 'suspended') && (
                          <span style={{ fontSize: 12, color: '#94a3b8' }}>
                            {i.childStatus === 'suspended' ? t('sp_managed_disabled', 'Disabled') : t('sp_managed_reclaimed', 'Reclaimed')}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

      <ManagedInviteModal
        open={showResellerModal}
        onClose={() => setShowResellerModal(false)}
        onSuccess={onInviteCreated}
        inviteType="reseller"
        groupings={groupings}
        parentName={account?.Name || t('sp_invite_your_partner', 'Your partner')}
      />
    </ResellerShell>
  )
}

// Per-row actions for a still-pending invite: copy the accept link (so the
// parent can paste it into their own email when the automated invite never
// arrives), resend, or revoke. "Copy link" reveals the URL inline as a
// fallback when the clipboard API is blocked (e.g. non-HTTPS context).
function PendingInviteActions({ invite, onResend, onRevoke }) {
  const { t } = useLanguage()
  const [copied, setCopied] = useState(false)
  const [reveal, setReveal] = useState(false)
  const url = invite.acceptUrl

  async function copyLink() {
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      // Clipboard unavailable — reveal the link so it can be selected manually.
      setReveal(true)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {url && (
          <button type="button" onClick={copyLink} style={{ ...secondaryBtn, color: '#2563eb', borderColor: '#bfdbfe' }}>
            {copied ? t('sp_managed_link_copied', 'Copied!') : t('sp_managed_copy_link', 'Copy link')}
          </button>
        )}
        <button type="button" onClick={onResend} style={secondaryBtn}>{t('sp_managed_resend', 'Resend')}</button>
        <button type="button" onClick={onRevoke} style={{ ...secondaryBtn, color: '#dc2626', borderColor: '#fecaca' }}>{t('sp_revoke', 'Revoke')}</button>
      </div>
      {url && (
        <button type="button" onClick={() => setReveal(r => !r)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontSize: 12, color: '#64748b' }}>
          {reveal ? t('sp_managed_hide_link', 'Hide link') : t('sp_managed_show_link', 'Show link')}
        </button>
      )}
      {reveal && url && (
        <code style={linkCode} onClick={e => {
          // One click selects the whole link for easy manual copy.
          const r = document.createRange(); r.selectNodeContents(e.currentTarget)
          const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(r)
        }}>{url}</code>
      )}
    </div>
  )
}

function TypeBadge({ type }) {
  const { t } = useLanguage()
  const course = type === 'course'
  return (
    <span style={{
      padding: '3px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
      background: course ? '#dbeafe' : '#dcfce7',
      color: course ? '#1d4ed8' : '#166534',
    }}>
      {course ? t('sp_managed_type_course', 'Course') : t('sp_dash_reseller_fallback', 'Reseller')}
    </span>
  )
}

function StatusBadge({ status }) {
  const { t } = useLanguage()
  const map = {
    sent: { bg: '#fef3c7', fg: '#92400e', label: t('sp_invite_sent', 'Sent') },
    opened: { bg: '#e0e7ff', fg: '#3730a3', label: t('sp_invite_opened', 'Opened') },
    accepted: { bg: '#dcfce7', fg: '#166534', label: t('sp_managed_accepted', 'Accepted') },
    expired: { bg: '#f1f5f9', fg: '#64748b', label: t('sp_managed_revoked', 'Revoked') },
  }
  const c = map[status] || { bg: '#f1f5f9', fg: '#475569', label: status }
  return <span style={{ padding: '3px 8px', background: c.bg, color: c.fg, borderRadius: 999, fontSize: 11, fontWeight: 600 }}>{c.label}</span>
}

const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }
const infoBox = { padding: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#475569', lineHeight: 1.5 }
const successBox = { padding: 16, marginBottom: 20, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8 }
const linkCode = { display: 'block', background: '#fff', padding: '8px 10px', borderRadius: 6, fontSize: 12, wordBreak: 'break-all', marginTop: 4 }
const empty = { padding: 40, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }
const tableWrap = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }
// Inline hotel/golf markup editor per managed account — lets the parent set
// the markup straight from the list (PUT /invites/:id/markup) without opening
// the child account. The child never sees these numbers.
function MarkupCell({ invite, editable, onSaved }) {
  const { t } = useLanguage()
  const [editing, setEditing] = useState(false)
  const [hotel, setHotel] = useState(String(invite.hotelMarkupPercent ?? 0))
  const [golf, setGolf] = useState(String(invite.golfMarkupPercent ?? 0))
  const [saving, setSaving] = useState(false)

  const summary = `${t('sp_markup_hotel_short', 'Hotel')} ${Number(invite.hotelMarkupPercent || 0)}% · ${t('sp_markup_golf_short', 'Golf')} ${Number(invite.golfMarkupPercent || 0)}%`

  async function save() {
    setSaving(true)
    try {
      await tournamentApi.put(`/portal/reseller/invites/${invite.id}/markup`, {
        hotelMarkupPercent: Number(hotel) || 0,
        golfMarkupPercent: Number(golf) || 0,
      })
      setEditing(false)
      onSaved?.()
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setSaving(false) }
  }

  if (!editable) return <span style={{ fontSize: 13, color: '#94a3b8' }}>{summary}</span>
  if (!editing) {
    return (
      <button type="button" onClick={() => setEditing(true)} title={t('sp_markup_edit', 'Edit markup')}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontSize: 13, color: '#0f172a' }}>
        {summary}<span style={{ color: '#2563eb', marginLeft: 6, fontSize: 12 }}>{t('edit', 'Edit')}</span>
      </button>
    )
  }
  const numInp = { width: 58, padding: '5px 6px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <label style={{ fontSize: 11, color: '#64748b' }}>{t('sp_markup_hotel_short', 'Hotel')}<input type="number" min={0} max={100} step="0.01" value={hotel} onChange={e => setHotel(e.target.value)} style={{ ...numInp, marginLeft: 4 }} />%</label>
      <label style={{ fontSize: 11, color: '#64748b' }}>{t('sp_markup_golf_short', 'Golf')}<input type="number" min={0} max={100} step="0.01" value={golf} onChange={e => setGolf(e.target.value)} style={{ ...numInp, marginLeft: 4 }} />%</label>
      <button type="button" onClick={save} disabled={saving} style={{ ...secondaryBtn, background: '#0f172a', color: '#fff', borderColor: '#0f172a' }}>{saving ? '…' : t('save', 'Save')}</button>
      <button type="button" onClick={() => { setEditing(false); setHotel(String(invite.hotelMarkupPercent ?? 0)); setGolf(String(invite.golfMarkupPercent ?? 0)) }} style={secondaryBtn}>{t('cancel', 'Cancel')}</button>
    </div>
  )
}

// Inline per-child menu-visibility editor. Lets the parent choose which portal
// menu items the managed child sees (PUT /invites/:id/nav-visibility) straight
// from the list — any item, not just the defaults. The summary shows how many
// are hidden; expanding reveals a toggle per menu item.
function MenuAccessCell({ invite, editable, onSaved }) {
  const { t } = useLanguage()
  const [editing, setEditing] = useState(false)
  const [hidden, setHidden] = useState(() => new Set(invite.hiddenNavKeys || []))
  const [saving, setSaving] = useState(false)

  const reset = () => setHidden(new Set(invite.hiddenNavKeys || []))
  const hiddenCount = (invite.hiddenNavKeys || []).length

  const summary = (
    <span style={{ fontSize: 12, color: hiddenCount ? '#475569' : '#94a3b8' }}>
      {hiddenCount === 0
        ? t('sp_menu_all_visible', 'All visible')
        : t('sp_menu_n_hidden', '{n} hidden', { n: hiddenCount })}
    </span>
  )

  function toggleVisible(key, visible) {
    setHidden(prev => {
      const next = new Set(prev)
      if (visible) next.delete(key)
      else next.add(key)
      return next
    })
  }

  async function save() {
    setSaving(true)
    try {
      await tournamentApi.put(`/portal/reseller/invites/${invite.id}/nav-visibility`, {
        hiddenNavKeys: [...hidden],
      })
      setEditing(false)
      onSaved?.()
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setSaving(false) }
  }

  if (!editable) return summary
  if (!editing) {
    return (
      <button type="button" onClick={() => { reset(); setEditing(true) }} title={t('sp_menu_edit', 'Edit menu access')}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
        {summary}<span style={{ color: '#2563eb', marginLeft: 6, fontSize: 12 }}>{t('edit', 'Edit')}</span>
      </button>
    )
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 200 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 220, overflowY: 'auto', paddingRight: 4 }}>
        {RESELLER_MENU_ITEMS.map(item => (
          <Toggle
            key={item.key}
            size="sm"
            checked={!hidden.has(item.key)}
            onChange={(v) => toggleVisible(item.key, v)}
            label={t(item.labelKey, item.fallback)}
          />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" onClick={save} disabled={saving} style={{ ...secondaryBtn, background: '#0f172a', color: '#fff', borderColor: '#0f172a' }}>{saving ? '…' : t('save', 'Save')}</button>
        <button type="button" onClick={() => { setEditing(false); reset() }} style={secondaryBtn}>{t('cancel', 'Cancel')}</button>
      </div>
    </div>
  )
}

const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 14 }
const tr = { borderBottom: '1px solid #f1f5f9' }
const th = { padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase', background: '#f8fafc' }
const td = { padding: '14px 16px', verticalAlign: 'top' }
const primaryBtn = { padding: '10px 20px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '6px 12px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }
const manageBtn = { padding: '6px 14px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const secondaryLink = { alignSelf: 'center', fontSize: 14, color: '#2563eb', textDecoration: 'none' }
const tabBar = { display: 'flex', gap: 4, background: '#fff', borderRadius: 8, padding: 4, border: '1px solid #e2e8f0' }
const tabBtn = (active) => ({ padding: '6px 12px', background: active ? '#0f172a' : 'transparent', color: active ? '#fff' : '#64748b', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' })
